<template>
  <v-dialog v-model="internalValue" max-width="600px" scrollable>
    <v-card class="rounded-xl border border-white-05 bg-surface-variant">
      <v-card-title class="d-flex align-center pa-4 border-b border-white-05">
        <span class="text-h6 font-weight-black">Quoted by</span>
        <v-spacer></v-spacer>
        <v-btn icon="mdi-close" variant="text" size="small" @click="internalValue = false"></v-btn>
      </v-card-title>

      <v-card-text class="pa-4" style="height: 600px;">
        <div v-if="loading && !posts.length" class="d-flex align-center justify-center h-100">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <div v-else-if="posts.length > 0">
          <PostItem 
            v-for="post in posts" 
            :key="post.uri" 
            :post="post"
            class="mb-4"
            @view-thread="navigateToThread"
            @view-profile="(handle) => { internalValue = false; $emit('view-profile', handle); }"
          />

          <div v-if="cursor" class="pa-4 text-center">
            <v-btn 
              variant="text" 
              color="primary" 
              class="text-none" 
              :loading="loading" 
              @click="loadMore"
            >
              Load More
            </v-btn>
          </div>
        </div>

        <div v-else class="d-flex flex-column align-center justify-center h-100 text-grey">
          <v-icon icon="mdi-format-quote-close-outline" size="x-large" class="mb-2"></v-icon>
          <span>No quotes found</span>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { getQuotes } from '@/services/bluesky';
import PostItem from './PostItem.vue';

const props = defineProps<{
  modelValue: boolean;
  postUri?: string;
}>();

const emit = defineEmits(['update:modelValue', 'view-thread', 'view-profile']);

const posts = ref<any[]>([]);
const loading = ref(false);
const cursor = ref<string | undefined>(undefined);

const internalValue = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

const fetchData = async (isLoadMore = false) => {
  if (!props.postUri) return;
  loading.value = true;
  
  try {
    const result = await getQuotes(props.postUri, 20, cursor.value);
    if (isLoadMore) posts.value = [...posts.value, ...result.posts];
    else posts.value = result.posts;
    
    cursor.value = result.cursor;
  } catch (error) {
    console.error('Failed to fetch quotes:', error);
  } finally {
    loading.value = false;
  }
};

const loadMore = () => fetchData(true);

const navigateToThread = (post: any) => {
  internalValue.value = false;
  emit('view-thread', post);
};

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    posts.value = [];
    cursor.value = undefined;
    fetchData();
  }
});
</script>

<style scoped>
.bg-surface-variant {
  background-color: rgba(11, 18, 25, 0.76) !important;
  backdrop-filter: blur(40px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(40px) saturate(180%) !important;
  color: white !important;
}
.border-white-05 {
  border-color: rgba(255, 255, 255, 0.1) !important;
}
:deep(.v-card-title) {
  color: white !important;
}
</style>
